import { useProgram } from '@/hooks/use-program'
import { useWallet } from '@solana/wallet-adapter-react'
import { useQuery } from '@tanstack/react-query'
import { PublicKey } from '@solana/web3.js'
import { useSupabase } from '@/components/auth/auth-provider'

export function usePlayerAccount() {
  const wallet = useWallet()
  const { publicKey } = wallet
  const { program } = useProgram()
  const { supabase, session } = useSupabase()

  return useQuery({
    queryKey: ['player-account', publicKey?.toBase58(), session?.user?.id],
    queryFn: async () => {
      // CRITICAL: Double-check session exists - this should never run without session due to enabled check,
      // but adding extra safety to prevent any RPC calls on login page
      if (!session?.user) {
        return null; // No session - return null immediately
      }

      // First, try to get the Web2 profile if it exists.
      let profile = null;

      // Backend API always enabled (migration complete)
      const { fetchProfile } = await import('@/lib/backend-api');
      try {
        const result = await fetchProfile({ userId: session.user.id });
        profile = result.profile;
      } catch (err) {
        console.error('Failed to fetch profile from backend:', err);
      }

      // CRITICAL: Skip Solana RPC calls entirely for username/password users
      // Check if user is Web3 user FIRST - if not, never make Solana calls
      const isWeb3User = session.user.user_metadata?.is_web3_user === true;

      // If user is NOT a Web3 user, return immediately (username/password login)
      if (!isWeb3User) {
        // Normalize the PDA field for Web2 users
        if (profile?.player_pda) {
          return { ...profile, pda: new PublicKey(profile.player_pda) };
        }
        return profile;
      }

      // Only make Solana calls if ALL conditions are met:
      // 1. User IS a Web3 user (checked above)
      // 2. Wallet is actually connected
      // 3. Public key exists
      // 4. Program exists
      if (!publicKey || !program || !wallet?.connected) {
        // Web3 user but wallet not connected - just return profile
        if (profile?.player_pda) {
          return { ...profile, pda: new PublicKey(profile.player_pda) };
        }
        return profile;
      }

      // Only reach here for Web3 users with connected wallet
      try {
        const [pda] = PublicKey.findProgramAddressSync(
          [Buffer.from('player'), publicKey.toBuffer()],
          program.programId
        );
        const onChainAccount = await (program.account as any).playerAccount.fetch(pda);

        // Merge the on-chain data with the profile data.
        return { ...profile, ...onChainAccount, pda };

      } catch (error) {
        // If the on-chain account doesn't exist, that's okay.
        // We might just have a Web2 profile.
        // Silent: no on-chain account is a valid state
        return profile;
      }
    },
    // CRITICAL: Only enable query if user is actually logged in (has a session)
    // Do NOT run this query on the login page - wait until user authenticates
    // This prevents unnecessary Solana RPC calls before login
    enabled: !!supabase && !!session?.user,
    staleTime: 5 * 60 * 1000, // 5 minutes - prevent excessive RPC calls
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
    refetchOnWindowFocus: false, // Don't refetch on tab focus
    refetchOnReconnect: false, // Don't refetch on network reconnect
    refetchInterval: false, // No automatic polling
  })
}